<template>
  <mf-card-container :title="isPdfContent ? '' : title" :description="isPdfContent ? '' : subtitle" :class="chartClass">
    <template v-if="!isPdfContent" v-slot:filters>
      <v-layout v-if="filterButton" align-center>
        <v-spacer />
        <export-data class="mx-2" :data-fetcher="getDataToExport" :filename="title" ghost />
        <span v-if="periodFilter" class="agrupamento-string pr-2 body-1">Agrupamento por:</span>
        <filter-type-toggle-button v-if="periodFilter" :type.sync="cardFilterType" />
        <chart-type-toggle-button :type.sync="cardChartTypeComputed" />
      </v-layout>
      <v-layout v-if="displayMode" align-center>
        <v-spacer />
        <export-data class="mx-2" :data-fetcher="getDataToExport" :filename="title" ghost />
        <chart-type-toggle-button :type.sync="cardChartTypeComputed" />
      </v-layout>
    </template>

    <mf-loading :loader="loader" loader-type="chart" :boolean-loader="chartLoading">
      <v-card-text class="pt-0">
        <div v-if="yMax" class="chart-container">
          <div class="chart-internal-container">
            <line-chart v-if="cardChartTypeComputed === 'line'" ref="lineChart" :chart-data="chartData" :options="chartConfig" :height="chartHeight" />
            <bar-chart v-else ref="barChart" :chart-data="chartData" :options="chartConfig" :height="chartHeight" />
          </div>
          <div v-if="controls && data.labels.length >= interval && !isPdfContent" class="text-right chart-controls">
            <mf-tooltip bottom>
              <template #activator="{ on }">
                <mf-button type="icon" icon="first_page" color="cinza-0" size="sm" v-on="on" @click="firstPosition" />
              </template>
              <span>Início</span>
            </mf-tooltip>

            <mf-tooltip bottom>
              <template #activator="{ on }">
                <mf-button
                  type="icon"
                  color="cinza-0"
                  icon="chevron_left"
                  size="sm"
                  @mousedown="clickAndHoldStart('intervalLeft')"
                  @mouseup="clickAndHoldStop"
                  @mouseleave="clickAndHoldStop"
                />
              </template>
              <span>{{ interval }} para a esquerda</span>
            </mf-tooltip>

            <mf-tooltip bottom>
              <template #activator="{ on }">
                <mf-button
                  type="icon"
                  color="cinza-0"
                  icon="arrow_left"
                  size="sm"
                  @mousedown="clickAndHoldStart('oneLeft')"
                  @mouseup="clickAndHoldStop"
                  @mouseleave="clickAndHoldStop"
                />
              </template>
              <span>1 para a esquerda</span>
            </mf-tooltip>

            <mf-tooltip bottom>
              <template #activator="{ on }">
                <mf-button
                  type="icon"
                  color="cinza-0"
                  icon="arrow_right"
                  size="sm"
                  @mousedown="clickAndHoldStart('oneRight')"
                  @mouseup="clickAndHoldStop"
                  @mouseleave="clickAndHoldStop"
                />
              </template>
              <span>1 para a direita</span>
            </mf-tooltip>

            <mf-tooltip bottom>
              <template #activator="{ on }">
                <mf-button
                  type="icon"
                  color="cinza-0"
                  icon="chevron_right"
                  size="sm"
                  @mousedown="clickAndHoldStart('intervalRight')"
                  @mouseup="clickAndHoldStop"
                  @mouseleave="clickAndHoldStop"
                />
              </template>
              <span>{{ interval }} para a direita</span>
            </mf-tooltip>

            <v-tooltip bottom>
              <template #activator="{ on }">
                <mf-button type="icon" color="cinza-0" icon="last_page" size="sm" @click="lastPosition" />
              </template>
              <span>Final</span>
            </v-tooltip>
            <span>{{ position + 1 }}/{{ maxPosition + 1 }}</span>
          </div>
        </div>
        <v-container v-else class="my-6">
          <v-row align="center">
            <v-col align="center">
              <empty-chart :paddingTop="emptyChartPaddingTop" />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </mf-loading>
  </mf-card-container>
</template>

<script>
import { defaultsDeep, max } from 'lodash'
import LineChart from '@/components/LineChart.vue'
import BarChart from '@/components/BarChart.vue'
import FilterTypeToggleButton from '@/components/FilterTypeToggleButton.vue'
import ChartTypeToggleButton from '@/components/ChartTypeToggleButton.vue'
import EmptyChart from '@/modules/mobile/desempenho/views/components/EmptyChart'
import { assertArray, localeString, abbreviation } from '@/config/helpers.js'
import ExportData from './export-data/ExportData.vue'

export default {
  components: {
    LineChart,
    BarChart,
    EmptyChart,
    FilterTypeToggleButton,
    ChartTypeToggleButton,
    ExportData,
  },
  props: {
    title: String,
    filterButton: Boolean,
    periodFilter: { type: Boolean, default: true },
    data: Object,
    isCurrency: Boolean,
    isPercent: Boolean,
    isInteger: Boolean,
    viewModel: String,
    filterType: String,
    loader: [String, Array],
    configUpdate: { type: Object, default: undefined },
    controls: { type: Boolean, default: true },
    legends: { type: Boolean, default: false },
    displayMode: Boolean,
    useChartType: {
      type: String,
      default: '',
    },
    chartHeight: {
      type: Number,
      default: 400,
    },
    cardHeight: {
      type: String,
      default: '',
    },
    chartLoading: {
      type: Boolean,
      default: false,
    },
    subtitle: {
      type: String,
      default: '',
    },
    chartClass: {
      type: String,
      default: '',
    },
    emptyChartPaddingTop: {
      type: String,
      default: () => '',
    },
    isPdfContent: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    cardChartType: 'bar',
    position: 0,
    interval: 13,
    timeout: null,
  }),
  computed: {
    cardFilterType: {
      get() {
        return this.filterType
      },
      set(value) {
        this.$emit('update:filterType', value)
      },
    },
    cardChartTypeComputed: {
      get() {
        return this.useChartType.length > 0 ? this.useChartType : this.cardChartType
      },
      set(value) {
        if (this.useChartType.length > 0) {
          this.$emit('update:useChartType', value)
        } else {
          this.cardChartType = value
        }
      },
    },
    chartConfig() {
      if (this.configUpdate) return defaultsDeep(this.configUpdate, this.configDefault)
      else return this.configDefault
    },
    configDefault() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        elements: {
          line: {
            tension: 0,
          },
          point: {
            radius: 1,
          },
        },
        layout: {
          padding: {
            top: 20,
            right: 60,
          },
        },
        animation: {
          duration: 0,
        },
        hover: {
          animationDuration: 0,
        },
        responsiveAnimationDuration: 0,
        legend: {
          display: this.legends,
          position: 'bottom',
        },
        tooltips: {
          enabled: false,
        },
        scales: {
          yAxes: [
            {
              ticks: {
                callback: this.valueFormatter,
                suggestedMax: this.yMax,
                beginAtZero: true,
              },
            },
          ],
          xAxes: [
            {
              barPercentage: 0.4,
              gridLines: { display: false },
            },
          ],
        },
        plugins: {
          datalabels: {
            backgroundColor: 'rgba(248, 249, 250, 0.82)',
            anchor: 'end',
            align: 'end',
            borderRadius: 4,
            color: 'rgba(0, 0, 0, 0.54)',
            formatter: this.valueFormatter,
          },
        },
      }
    },
    maxPosition() {
      if (this.data) {
        return Math.abs(this.data.labels.length - this.interval)
      }
      return this.interval
    },
    chartData() {
      if (this.data.datasets.length && this.controls) {
        let startPosition = this.position
        let endPosition = this.position + this.interval
        if (this.data.labels.length < this.interval) {
          startPosition = 0
          endPosition = this.data.labels.length
        }
        const newLabels = this.data.labels.slice(startPosition, endPosition)
        const newDatasets = []
        for (const dataset of assertArray(this.data.datasets)) {
          const newData = dataset.data.slice(startPosition, endPosition)
          newDatasets.push({
            ...dataset,
            data: [...newData],
            datalabels: { display: true },
          })
        }
        return {
          ...this.data,
          labels: [...newLabels],
          datasets: newDatasets,
        }
      } else return this.data
    },
    yMax() {
      if (this.data.datasets[0]) return max(this.data.datasets[0].data)
      return 0
    },
  },
  watch: {
    'data.labels'(newValue, oldValue) {
      const suggestedMax = this.yMax > 10 ? this.yMax : 10
      this.position = this.maxPosition
      if (oldValue.length && (this.$refs.lineChart || this.$refs.barChart)) {
        if (this.cardChartTypeComputed === 'line') {
          let chart = this.$refs.lineChart.$data._chart
          chart.options.scales.yAxes[0].ticks.suggestedMax = suggestedMax
          chart.update()
        }
        if (this.cardChartTypeComputed === 'bar') {
          let chart = this.$refs.barChart.$data._chart
          chart.options.scales.yAxes[0].ticks.suggestedMax = suggestedMax
          chart.update()
        }
      }
    },
  },
  methods: {
    getDataToExport() {
      const headers = ['Periodo']
      const rows = []
      this.data.datasets.forEach((dataset) => headers.push(dataset.label) || '-')

      this.data.labels.forEach((value, index) => {
        const periodCol = this.filterType === 'semanal' ? `${value[0]} - ${value[1]}` : value
        const dataCols = this.data.datasets.map((dataset) => dataset.data[index])
        rows.push([periodCol, ...dataCols])
      })
      return { headers, rows }
    },
    firstPosition() {
      this.position = 0
    },
    oneLeft() {
      if (this.position > 0) this.position -= 1
    },
    intervalLeft() {
      if (this.position > this.interval) this.position -= this.interval
      else if (this.position > 0) this.position = 0
    },
    oneRight() {
      if (this.position < this.maxPosition) this.position += 1
    },
    intervalRight() {
      if (this.position < this.maxPosition - this.interval) this.position += this.interval
      else if (this.position < this.maxPosition) this.position = this.maxPosition
    },
    lastPosition() {
      this.position = this.maxPosition
    },
    clickAndHoldStart(method) {
      this[method]()
      this.timeout = setTimeout(() => {
        this.clickAndHoldStart(method)
      }, 100)
    },
    clickAndHoldStop() {
      clearTimeout(this.timeout)
    },
    valueFormatter(value) {
      if (this.isCurrency) return `R$ ${abbreviation(value, 2)}`
      if (this.isPercent && value < 1) return `${localeString(value, 3)}%`
      if (this.isPercent) return `${localeString(value, 1)}%`
      if (this.isInteger) return localeString(value, 0)
      return localeString(value, 1)
    },
  },
}
</script>

<style lang="scss">
.chart-card {
  min-height: 250px;
  .chart-container {
    // overflow-x: scroll;
    width: 100%;
    .chart-internal-container {
      position: relative;
      left: 16px;
      bottom: 14px;
    }
  }
  .agrupamento-string {
    color: rgba(0, 0, 0, 0.54);
  }
  .chart-controls {
    user-select: none;
  }
  .subtitle {
    font-size: 16px;
    color: $cinza-0;
  }
}
</style>
